/**
 * One address a customer can be given on a submission.
 *
 * A submission cannot work out which address is relevant: it links to a customer
 * and a payer, never to an order. So somebody chooses, and the lab sheet and the
 * recommendation reports all print what was chosen.
 */
export interface TenantAddressModel {
  id: number;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  town: string | null;
  province: string | null;
  postalCode: string | null;

  /**
   * The customer's current address, the one the SAP sync last wrote.
   *
   * Every other row is either an address SAP has replaced or one captured by hand
   * for a delivery, and nothing in the data tells those apart. They are all listed:
   * the user choosing deliberately is the point, and the alternative is the system
   * choosing silently, which is what went wrong before.
   */
  current: boolean;
}

/** What the customer has, and what this submission holds. */
export interface SubmissionAddressesModel {
  /** Null where nobody has chosen, which means the current address is used. */
  selectedAddressId: number | null;
  addresses: TenantAddressModel[];
}

/** The fields an address is typed into. All optional: an incomplete address is still an address. */
export interface NewTenantAddressModel {
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  town: string | null;
  province: string | null;
  postalCode: string | null;
}

/**
 * An address on one line, for a dropdown option.
 *
 * Empty fields are dropped rather than rendered as gaps, so an address SAP sent with
 * no line 2 reads as "Street, Town, 1234" instead of "Street, , Town, , 1234".
 */
export function formatTenantAddress(address: TenantAddressModel | null | undefined): string {
  if (!address) {
    return '';
  }
  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.addressLine3,
    address.town,
    address.province,
    address.postalCode,
  ].filter(part => !!part && part.trim().length > 0);

  // An address with nothing in it still needs a label, or the option is a blank row
  // the user cannot tell apart from any other blank row.
  return parts.length ? parts.join(', ') : `Address ${address.id}`;
}
